import React from 'react';
import { Platform, ScrollView, TouchableOpacity, View } from 'react-native';
import Ionicons from '@expo/vector-icons/Ionicons';

import { Typography } from '../ui';

type PathOption = {
  id: string;
  name: string;
};

export const getLivesCalendarFilterLabels = (language: string) => ({
  all: language === 'ru' ? 'Все' : language === 'es' ? 'Todos' : 'All',
  livestreams: language === 'ru' ? 'Live-сессии' : language === 'es' ? 'Sesiones en vivo' : 'Live Sessions',
  offline: language === 'ru' ? 'Офлайн' : language === 'es' ? 'Presencial' : 'Offline',
  paths: language === 'ru' ? 'Направления' : language === 'es' ? 'Caminos' : 'Directions',
  selectPath: language === 'ru' ? 'ВЫБЕРИТЕ НАПРАВЛЕНИЕ:' : language === 'es' ? 'ELIGE UN CAMINO:' : 'SELECT DIRECTION:',
});

type LivesCalendarFiltersProps = {
  accentColor: string;
  activeTypeFilters: string[];
  compact?: boolean;
  isDark: boolean;
  labels: ReturnType<typeof getLivesCalendarFilterLabels>;
  onClearPaths: () => void;
  onTogglePath: (path: string) => void;
  onToggleType: (type: string) => void;
  openDropdown: 'paths' | null;
  pathOptions: PathOption[];
  selectedPaths: string[];
  setOpenDropdown: React.Dispatch<React.SetStateAction<'paths' | null>>;
  showDropdownPanel?: boolean;
  showPills?: boolean;
  themeColors: {
    textPrimary: string;
    textSecondary: string;
  };
};

export const LivesCalendarFilters: React.FC<LivesCalendarFiltersProps> = ({
  accentColor,
  activeTypeFilters,
  compact = false,
  isDark,
  labels,
  onClearPaths,
  onTogglePath,
  onToggleType,
  openDropdown,
  pathOptions,
  selectedPaths,
  setOpenDropdown,
  showDropdownPanel = true,
  showPills = true,
  themeColors,
}) => {
  // AMB-231 — hide the «Live-практики» filter pill when the flag is off.
  const renderFilterPill = (label: string, isActive: boolean, onPress: () => void, hasDropdown = false) => (
    <TouchableOpacity
      activeOpacity={0.7}
      onPress={onPress}
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        paddingHorizontal: 14,
        paddingVertical: 7,
        borderRadius: 20,
        borderWidth: 1,
        borderColor: isActive ? accentColor : (isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'),
        backgroundColor: isActive ? 'rgba(233, 130, 69,0.08)' : 'transparent',
        ...Platform.select({ web: { cursor: 'pointer', transition: 'all 0.15s ease' } as any }),
      }}
    >
      <Typography
        variant="body"
        style={{ color: isActive ? accentColor : themeColors.textSecondary, fontWeight: isActive ? '700' : '500', fontSize: 13 } as any}
      >
        {label}
      </Typography>
      {hasDropdown && (
        <Ionicons name="chevron-down" size={14} color={isActive ? accentColor : themeColors.textSecondary} />
      )}
    </TouchableOpacity>
  );

  return (
    <>
      {showPills && (
        <View
          style={{
            borderBottomWidth: compact || openDropdown === 'paths' ? 0 : 1,
            borderBottomColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
            paddingHorizontal: compact ? 0 : 12,
            paddingBottom: compact ? 0 : 14,
            flex: compact ? 1 : undefined,
            minWidth: compact ? 0 : undefined,
          }}
        >
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            style={compact ? { flex: 1, marginHorizontal: 16 } : undefined}
            contentContainerStyle={{ alignItems: 'center', gap: 8, paddingHorizontal: 4 }}
          >
            {renderFilterPill(labels.paths, selectedPaths.length > 0 || openDropdown === 'paths', () => setOpenDropdown(prev => prev === 'paths' ? null : 'paths'), true)}
            {/* CORE-1865 — одна «Live-сессии» на эфиры и комнаты: отдельного продукта «Live-практика» нет. */}
            {renderFilterPill(labels.livestreams, activeTypeFilters.includes('livestream') || activeTypeFilters.includes('liveroom'), () => onToggleType('livestream'))}
            {renderFilterPill(labels.offline, activeTypeFilters.includes('offline'), () => onToggleType('offline'))}
          </ScrollView>
        </View>
      )}

      {showDropdownPanel && openDropdown === 'paths' && (
        <View
          style={{
            padding: 20,
            backgroundColor: isDark ? 'rgba(255,255,255,0.02)' : '#F5F7F9',
            borderBottomWidth: 1,
            borderBottomColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
          }}
        >
          <Typography
            variant="overline"
            style={{ color: themeColors.textSecondary, marginBottom: 16, letterSpacing: 1.5, fontSize: 10, fontWeight: '700' } as any}
          >
            {labels.selectPath}
          </Typography>
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 10 }}>
            <TouchableOpacity
              style={{
                paddingHorizontal: 18,
                paddingVertical: 10,
                borderRadius: 20,
                backgroundColor: selectedPaths.length === 0 ? accentColor : (isDark ? 'rgba(255,255,255,0.05)' : '#FFF'),
                borderWidth: 1,
                borderColor: selectedPaths.length === 0 ? accentColor : (isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'),
                ...Platform.select({ web: { cursor: 'pointer', transition: 'all 0.15s ease' } as any }),
              }}
              onPress={onClearPaths}
            >
              <Typography
                variant="body"
                style={{ color: selectedPaths.length === 0 ? '#FFF' : themeColors.textPrimary, fontSize: 14, fontWeight: selectedPaths.length === 0 ? '600' : '400' } as any}
              >
                {labels.all}
              </Typography>
            </TouchableOpacity>

            {pathOptions.map(path => (
              <TouchableOpacity
                key={path.id}
                style={{
                  paddingHorizontal: 18,
                  paddingVertical: 10,
                  borderRadius: 20,
                  backgroundColor: selectedPaths.includes(path.id) ? accentColor : (isDark ? 'rgba(255,255,255,0.05)' : '#FFF'),
                  borderWidth: 1,
                  borderColor: selectedPaths.includes(path.id) ? accentColor : (isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'),
                  ...Platform.select({ web: { cursor: 'pointer', transition: 'all 0.15s ease' } as any }),
                }}
                onPress={() => onTogglePath(path.id)}
              >
                <Typography
                  variant="body"
                  style={{ color: selectedPaths.includes(path.id) ? '#FFF' : themeColors.textPrimary, fontSize: 14, fontWeight: selectedPaths.includes(path.id) ? '600' : '400' } as any}
                >
                  {path.name}
                </Typography>
              </TouchableOpacity>
            ))}
          </View>
        </View>
      )}
    </>
  );
};
